import React, { useRef, useState } from 'react';
import clsx from 'clsx';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button, SelectField, TextField } from 'components/ui';
import { RequiredMark } from 'components/ui/FieldShell';
import { PICKER_MARITAL_STATUS } from 'constants/picker';
import updateSclaAppFamily from 'features/scla/api/updateSclaAppFamily';
import { IUpdateSclaFamily } from 'features/scla/types';
import {
  emailError,
  formatPhone,
  isValidEmail,
  isValidPhone,
  phoneDigits,
  phoneError,
} from 'features/scla/utils/contactFields';
import {
  getSclaAppBackgroundPath,
  getSclaAppSchoolPath,
} from 'utils/navigation';
import ChoiceChips from '../components/steps/ChoiceChips';
import SclaStepCard from '../components/steps/SclaStepCard';
import styles from '../components/steps/SclaStep.module.scss';
import {
  SCLA_EMPLOYMENT_STATUSES,
  SCLA_PARENT_EDUCATION,
  SCLA_SALARY_RANGES,
  keptLegacyValue,
  parentEducationValue,
} from '../config/applicationOptions';
import useAutosave, { useSectionReady } from '../hooks/useAutosave';
import useSclaSectionPath from '../hooks/useSclaSectionPath';
import { SclaApplicationOutletContext } from './SclaAppLayout';
import clearFilledErrors from '../utils/clearFilledErrors';

type PersonKey = 'mother' | 'father' | 'guardian';

type FamilyForm = IUpdateSclaFamily & {
  motherSalaryChoice: string;
  fatherSalaryChoice: string;
  guardianSalaryChoice: string;
};

type ContactMemory = {
  motherEmail: string;
  motherPhone: string;
  fatherEmail: string;
  fatherPhone: string;
  guardianEmail: string;
  guardianPhone: string;
};

const FOCUS_ORDER: Array<[string, string]> = [
  ['parentsMaritalStatus', 'scla-marital'],
  ['household', 'scla-family-required'],
  ['motherName', 'mother-name'],
  ['motherPhoneNumber', 'mother-phone'],
  ['motherEmail', 'mother-email'],
  ['fatherName', 'father-name'],
  ['fatherPhoneNumber', 'father-phone'],
  ['fatherEmail', 'father-email'],
  ['guardianRelationship', 'guardian-rel'],
  ['guardianName', 'guardian-name'],
  ['guardianPhoneNumber', 'guardian-phone'],
  ['guardianEmail', 'guardian-email'],
];

const PEOPLE: Array<{ key: PersonKey; label: string }> = [
  { key: 'mother', label: 'Mother' },
  { key: 'father', label: 'Father' },
  { key: 'guardian', label: 'Other guardian' },
];

function text(value: string | null | undefined) {
  return value || '';
}

function includedFlag(
  flag: boolean | null | undefined,
  filled: boolean,
  fallback: boolean,
) {
  if (flag === true || flag === false) return flag;
  if (filled) return true;
  return fallback;
}

function mapFamily(application: SclaApplicationOutletContext['application']): FamilyForm {
  const family = application.familyInfo;
  const motherFilled = Boolean(text(family.motherName));
  const fatherFilled = Boolean(text(family.fatherName));
  const guardianFilled = Boolean(text(family.guardianName));
  return {
    parentsMaritalStatus: text(family.parentsMaritalStatus),
    numOfBrothers: text(family.numOfBrothers) || '0',
    numOfSisters: text(family.numOfSisters) || '0',
    fatherStatus: text(family.fatherStatus),
    fatherName: text(family.fatherName),
    fatherPlaceOfBirth: text(family.fatherPlaceOfBirth),
    fatherPhoneNumber: formatPhone(text(family.fatherPhoneNumber)),
    fatherEmployer: text(family.fatherEmployer),
    fatherOccupation: text(family.fatherOccupation),
    fatherEducation: parentEducationValue(text(family.fatherEducation)),
    fatherSalary: text(family.fatherSalary),
    fatherEmail: text(family.fatherEmail),
    fatherSalaryChoice: SCLA_SALARY_RANGES.includes(text(family.fatherSalary))
      ? text(family.fatherSalary)
      : '',
    motherStatus: text(family.motherStatus),
    motherName: text(family.motherName),
    motherPlaceOfBirth: text(family.motherPlaceOfBirth),
    motherPhoneNumber: formatPhone(text(family.motherPhoneNumber)),
    motherEmployer: text(family.motherEmployer),
    motherOccupation: text(family.motherOccupation),
    motherEducation: parentEducationValue(text(family.motherEducation)),
    motherSalary: text(family.motherSalary),
    motherEmail: text(family.motherEmail),
    motherSalaryChoice: SCLA_SALARY_RANGES.includes(text(family.motherSalary))
      ? text(family.motherSalary)
      : '',
    guardianRelationship: text(family.guardianRelationship),
    guardianName: text(family.guardianName),
    guardianPlaceOfBirth: text(family.guardianPlaceOfBirth),
    guardianPhoneNumber: formatPhone(text(family.guardianPhoneNumber)),
    guardianEmployer: text(family.guardianEmployer),
    guardianOccupation: text(family.guardianOccupation),
    guardianEducation: parentEducationValue(text(family.guardianEducation)),
    guardianSalary: text(family.guardianSalary),
    guardianEmail: text(family.guardianEmail),
    guardianSalaryChoice: SCLA_SALARY_RANGES.includes(text(family.guardianSalary))
      ? text(family.guardianSalary)
      : '',
    motherIncluded: includedFlag(family.motherIncluded, motherFilled, false),
    fatherIncluded: includedFlag(family.fatherIncluded, fatherFilled, false),
    guardianIncluded: includedFlag(family.guardianIncluded, guardianFilled, false),
  };
}

function storedSalary(choice: string, previous: string) {
  return choice || previous;
}

function rememberedContact(value: string, kind: 'email' | 'phone'): string {
  if (kind === 'email') return isValidEmail(value) ? value.trim() : '';
  return isValidPhone(value) ? formatPhone(value) : '';
}

function contactMemory(form: FamilyForm): ContactMemory {
  return {
    motherEmail: rememberedContact(form.motherEmail, 'email'),
    motherPhone: rememberedContact(form.motherPhoneNumber, 'phone'),
    fatherEmail: rememberedContact(form.fatherEmail, 'email'),
    fatherPhone: rememberedContact(form.fatherPhoneNumber, 'phone'),
    guardianEmail: rememberedContact(form.guardianEmail, 'email'),
    guardianPhone: rememberedContact(form.guardianPhoneNumber, 'phone'),
  };
}

function blankPerson(key: PersonKey): Partial<IUpdateSclaFamily> {
  if (key === 'mother') {
    return {
      motherName: '',
      motherPlaceOfBirth: '',
      motherPhoneNumber: '',
      motherEmployer: '',
      motherOccupation: '',
      motherEducation: '',
      motherSalary: '',
      motherEmail: '',
      motherStatus: '',
    };
  }
  if (key === 'father') {
    return {
      fatherName: '',
      fatherPlaceOfBirth: '',
      fatherPhoneNumber: '',
      fatherEmployer: '',
      fatherOccupation: '',
      fatherEducation: '',
      fatherSalary: '',
      fatherEmail: '',
      fatherStatus: '',
    };
  }
  return {
    guardianRelationship: '',
    guardianName: '',
    guardianPlaceOfBirth: '',
    guardianPhoneNumber: '',
    guardianEmployer: '',
    guardianOccupation: '',
    guardianEducation: '',
    guardianSalary: '',
    guardianEmail: '',
  };
}

function revealField(id: string) {
  const node = document.getElementById(id);
  if (!node) return;
  let shell: HTMLElement = node;
  let current: HTMLElement | null = node;
  while (current.parentElement) {
    if (current.parentElement.querySelector(`label[for="${id}"]`)) {
      shell = current.parentElement;
      break;
    }
    current = current.parentElement;
  }
  shell.scrollIntoView({ behavior: 'smooth', block: 'center' });
  node.focus();
}

function clearError(
  current: Record<string, string>,
  key: string,
): Record<string, string> {
  if (!current[key]) return current;
  return { ...current, [key]: '' };
}

export default function SclaApplicationFamily() {
  const { application, setApplication, readOnly } =
    useOutletContext<SclaApplicationOutletContext>();
  const sectionPath = useSclaSectionPath();
  const navigate = useNavigate();
  const [form, setForm] = useState(() => mapFamily(application));
  const ready = useSectionReady(application._id, () => setForm(mapFamily(application)));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const lastValid = useRef<ContactMemory>(contactMemory(form));

  const patch = (partial: Partial<FamilyForm>) => {
    setForm(current => ({ ...current, ...partial }));
    setErrors(current => clearFilledErrors(current, partial, {}));
  };

  const save = async () => {
    const {
      motherSalaryChoice,
      fatherSalaryChoice,
      guardianSalaryChoice,
      motherSalary,
      fatherSalary,
      guardianSalary,
      ...rest
    } = form;
    const saved: ContactMemory = lastValid.current;
    const payload: IUpdateSclaFamily = {
      ...rest,
      motherIncluded: Boolean(form.motherIncluded),
      fatherIncluded: Boolean(form.fatherIncluded),
      guardianIncluded: Boolean(form.guardianIncluded),
      motherSalary: form.motherIncluded
        ? storedSalary(motherSalaryChoice, keptLegacyValue(motherSalary, SCLA_SALARY_RANGES))
        : '',
      fatherSalary: form.fatherIncluded
        ? storedSalary(fatherSalaryChoice, keptLegacyValue(fatherSalary, SCLA_SALARY_RANGES))
        : '',
      guardianSalary: form.guardianIncluded
        ? storedSalary(guardianSalaryChoice, keptLegacyValue(guardianSalary, SCLA_SALARY_RANGES))
        : '',
      motherEmail: form.motherIncluded ? saved.motherEmail : '',
      motherPhoneNumber: form.motherIncluded ? saved.motherPhone : '',
      fatherEmail: form.fatherIncluded ? saved.fatherEmail : '',
      fatherPhoneNumber: form.fatherIncluded ? saved.fatherPhone : '',
      guardianEmail: form.guardianIncluded ? saved.guardianEmail : '',
      guardianPhoneNumber: form.guardianIncluded ? saved.guardianPhone : '',
      ...(form.motherIncluded ? {} : blankPerson('mother')),
      ...(form.fatherIncluded ? {} : blankPerson('father')),
      ...(form.guardianIncluded ? {} : blankPerson('guardian')),
    };
    const response = await updateSclaAppFamily(application._id, payload);
    setApplication(response);
    return true;
  };

  const { flush } = useAutosave({
    serialized: JSON.stringify(form),
    enabled: !readOnly && ready,
    save,
  });

  const changeCount = (key: 'numOfBrothers' | 'numOfSisters', delta: number) => {
    const next = Math.max(0, Number(form[key] || 0) + delta);
    patch({ [key]: String(next) });
  };

  const toggle = (who: PersonKey) => {
    const includedKey = `${who}Included` as const;
    const on = !form[includedKey];
    patch({ [includedKey]: on });
    if (on) setErrors(current => clearError(current, 'household'));
  };

  const changePhone = (
    key: 'motherPhoneNumber' | 'fatherPhoneNumber' | 'guardianPhoneNumber',
    memoryKey: 'motherPhone' | 'fatherPhone' | 'guardianPhone',
    value: string,
  ) => {
    const formatted = formatPhone(value);
    patch({ [key]: formatted });
    setErrors(current => ({ ...current, [key]: phoneError(formatted, false) }));
    if (!phoneDigits(formatted)) lastValid.current[memoryKey] = '';
    else if (isValidPhone(formatted)) lastValid.current[memoryKey] = formatted;
  };

  const changeEmail = (
    key: 'motherEmail' | 'fatherEmail' | 'guardianEmail',
    memoryKey: 'motherEmail' | 'fatherEmail' | 'guardianEmail',
    value: string,
  ) => {
    patch({ [key]: value });
    setErrors(current => ({ ...current, [key]: emailError(value, false) }));
    if (isValidEmail(value)) lastValid.current[memoryKey] = value.trim();
  };

  const handleContinue = async () => {
    const posted = await flush();
    const nextErrors: Record<string, string> = {};
    if (!form.parentsMaritalStatus) {
      nextErrors.parentsMaritalStatus = 'Choose the parents marital status.';
    }
    const someone = form.motherIncluded || form.fatherIncluded || form.guardianIncluded;
    if (!someone) nextErrors.household = 'Turn on at least one parent or guardian';
    if (form.motherIncluded && !form.motherName.trim()) {
      nextErrors.motherName = "Enter Mother's name.";
    }
    if (form.fatherIncluded && !form.fatherName.trim()) {
      nextErrors.fatherName = "Enter Father's name.";
    }
    if (form.guardianIncluded && !form.guardianRelationship.trim()) {
      nextErrors.guardianRelationship = "Enter the guardian's relationship.";
    }
    if (form.guardianIncluded && !form.guardianName.trim()) {
      nextErrors.guardianName = "Enter the guardian's name.";
    }
    if (form.motherIncluded && phoneError(form.motherPhoneNumber, false)) {
      nextErrors.motherPhoneNumber = phoneError(form.motherPhoneNumber, false);
    }
    if (form.fatherIncluded && phoneError(form.fatherPhoneNumber, false)) {
      nextErrors.fatherPhoneNumber = phoneError(form.fatherPhoneNumber, false);
    }
    if (form.guardianIncluded && phoneError(form.guardianPhoneNumber, false)) {
      nextErrors.guardianPhoneNumber = phoneError(form.guardianPhoneNumber, false);
    }
    if (form.motherIncluded && emailError(form.motherEmail, false)) {
      nextErrors.motherEmail = emailError(form.motherEmail, false);
    }
    if (form.fatherIncluded && emailError(form.fatherEmail, false)) {
      nextErrors.fatherEmail = emailError(form.fatherEmail, false);
    }
    if (form.guardianIncluded && emailError(form.guardianEmail, false)) {
      nextErrors.guardianEmail = emailError(form.guardianEmail, false);
    }
    setErrors(nextErrors);
    const first = FOCUS_ORDER.find(([key]) => nextErrors[key]);
    if (first) window.setTimeout(() => revealField(first[1]), 0);
    const ok = posted && !first;
    if (ok) navigate(sectionPath(getSclaAppSchoolPath()));
    return ok;
  };

  const employmentOptions = SCLA_EMPLOYMENT_STATUSES.map(item => ({
    label: item,
    value: item,
  }));
  const salaryOptions = SCLA_SALARY_RANGES.map(item => ({ label: item, value: item }));
  const educationOptions = SCLA_PARENT_EDUCATION.map(item => ({ label: item, value: item }));
  const maritalOptions = PICKER_MARITAL_STATUS.map(item => ({
    label: item.label,
    value: item.value,
  }));

  return (
    <SclaStepCard
      title="Family"
      intro="Tell us who's in your household. Turn off anyone who doesn't apply; their fields go away."
      backTo={sectionPath(getSclaAppBackgroundPath())}
      onContinue={handleContinue}
      continueLabel="Save and continue →"
      readOnly={readOnly}
    >
      <fieldset className={styles.group} disabled={readOnly}>
        <legend>Siblings</legend>
        <div className={styles.grid}>
          <div>
            <p className={styles.question} id="scla-brothers">
              Brothers
              <RequiredMark />
            </p>
            <div className={styles.counter}>
              <Button type="button" variant="secondary" aria-label="Fewer brothers" onClick={() => changeCount('numOfBrothers', -1)}>−</Button>
              <b>{form.numOfBrothers}</b>
              <Button type="button" variant="secondary" aria-label="More brothers" onClick={() => changeCount('numOfBrothers', 1)}>+</Button>
            </div>
          </div>
          <div>
            <p className={styles.question} id="scla-sisters">
              Sisters
              <RequiredMark />
            </p>
            <div className={styles.counter}>
              <Button type="button" variant="secondary" aria-label="Fewer sisters" onClick={() => changeCount('numOfSisters', -1)}>−</Button>
              <b>{form.numOfSisters}</b>
              <Button type="button" variant="secondary" aria-label="More sisters" onClick={() => changeCount('numOfSisters', 1)}>+</Button>
            </div>
          </div>
          <SelectField
            id="scla-marital"
            label="Parents marital status"
            required
            value={form.parentsMaritalStatus || null}
            options={maritalOptions}
            error={errors.parentsMaritalStatus}
            onChange={value => {
              patch({ parentsMaritalStatus: value || '' });
              if (value) setErrors(current => clearError(current, 'parentsMaritalStatus'));
            }}
          />
        </div>
      </fieldset>
      <fieldset className={styles.group} disabled={readOnly}>
        <legend id="scla-parents">Parents and guardians</legend>
        {errors.household ? (
          <p id="scla-family-required" className={styles.fieldError} role="alert" tabIndex={-1}>
            {errors.household}
          </p>
        ) : null}
        {PEOPLE.map(({ key, label }) => {
          const on = Boolean(form[`${key}Included`]);
          return (
            <div className={clsx(styles.person, !on && styles.personOff)} key={key}>
              <div className={styles.personHead}>
                <b>{label}</b>
                <button
                  type="button"
                  className={clsx(styles.toggle, on && styles.toggleOn)}
                  role="switch"
                  aria-checked={on}
                  aria-label={`${label} is part of my life`}
                  disabled={readOnly}
                  onClick={() => toggle(key)}
                >
                  <span>{on ? 'In my life / part of my household' : 'Not applicable'}</span>
                  <span className={styles.toggleTrack} aria-hidden="true" />
                </button>
              </div>
              {on && key === 'mother' ? (
                <div className={clsx(styles.grid, styles.personBody)}>
                  <div className={styles.span}>
                    <TextField
                      id="mother-name"
                      label="Full name"
                      required
                      value={form.motherName}
                      error={errors.motherName}
                      onChange={value => {
                        patch({ motherName: value });
                        if (value.trim()) setErrors(current => clearError(current, 'motherName'));
                      }}
                    />
                  </div>
                  <TextField id="mother-phone" label="Cell phone" type="tel" value={form.motherPhoneNumber} error={errors.motherPhoneNumber} onChange={value => changePhone('motherPhoneNumber', 'motherPhone', value)} />
                  <TextField id="mother-email" label="Email" type="email" value={form.motherEmail} error={errors.motherEmail} onChange={value => changeEmail('motherEmail', 'motherEmail', value)} />
                  <div className={styles.span}>
                    <ChoiceChips label="Employment status" options={employmentOptions} value={form.motherStatus} disabled={readOnly} onChange={value => patch({ motherStatus: value })} />
                    {keptLegacyValue(form.motherStatus, SCLA_EMPLOYMENT_STATUSES) ? <p className={styles.legacy}>{`Previous answer: ${form.motherStatus}`}</p> : null}
                  </div>
                  <TextField id="mother-job" label="Job / occupation" value={form.motherOccupation} onChange={value => patch({ motherOccupation: value })} />
                  <TextField id="mother-employer" label="Employer" value={form.motherEmployer} onChange={value => patch({ motherEmployer: value })} />
                  <SelectField id="mother-education" label="Highest grade completed" value={form.motherEducation || null} options={educationOptions} onChange={value => patch({ motherEducation: value || '' })} />
                  <TextField id="mother-birth" label="Place of birth" value={form.motherPlaceOfBirth} onChange={value => patch({ motherPlaceOfBirth: value })} />
                  <div className={styles.span}>
                    <ChoiceChips label="Annual income range" options={salaryOptions} value={form.motherSalaryChoice} disabled={readOnly} onChange={value => patch({ motherSalaryChoice: value })} />
                    {keptLegacyValue(form.motherSalary, SCLA_SALARY_RANGES) ? <p className={styles.legacy}>{`Previous answer: ${form.motherSalary}`}</p> : null}
                  </div>
                </div>
              ) : null}
              {on && key === 'father' ? (
                <div className={clsx(styles.grid, styles.personBody)}>
                  <div className={styles.span}>
                    <TextField
                      id="father-name"
                      label="Full name"
                      required
                      value={form.fatherName}
                      error={errors.fatherName}
                      onChange={value => {
                        patch({ fatherName: value });
                        if (value.trim()) setErrors(current => clearError(current, 'fatherName'));
                      }}
                    />
                  </div>
                  <TextField id="father-phone" label="Cell phone" type="tel" value={form.fatherPhoneNumber} error={errors.fatherPhoneNumber} onChange={value => changePhone('fatherPhoneNumber', 'fatherPhone', value)} />
                  <TextField id="father-email" label="Email" type="email" value={form.fatherEmail} error={errors.fatherEmail} onChange={value => changeEmail('fatherEmail', 'fatherEmail', value)} />
                  <div className={styles.span}>
                    <ChoiceChips label="Employment status" options={employmentOptions} value={form.fatherStatus} disabled={readOnly} onChange={value => patch({ fatherStatus: value })} />
                    {keptLegacyValue(form.fatherStatus, SCLA_EMPLOYMENT_STATUSES) ? <p className={styles.legacy}>{`Previous answer: ${form.fatherStatus}`}</p> : null}
                  </div>
                  <TextField id="father-job" label="Job / occupation" value={form.fatherOccupation} onChange={value => patch({ fatherOccupation: value })} />
                  <TextField id="father-employer" label="Employer" value={form.fatherEmployer} onChange={value => patch({ fatherEmployer: value })} />
                  <SelectField id="father-education" label="Highest grade completed" value={form.fatherEducation || null} options={educationOptions} onChange={value => patch({ fatherEducation: value || '' })} />
                  <TextField id="father-birth" label="Place of birth" value={form.fatherPlaceOfBirth} onChange={value => patch({ fatherPlaceOfBirth: value })} />
                  <div className={styles.span}>
                    <ChoiceChips label="Annual income range" options={salaryOptions} value={form.fatherSalaryChoice} disabled={readOnly} onChange={value => patch({ fatherSalaryChoice: value })} />
                    {keptLegacyValue(form.fatherSalary, SCLA_SALARY_RANGES) ? <p className={styles.legacy}>{`Previous answer: ${form.fatherSalary}`}</p> : null}
                  </div>
                </div>
              ) : null}
              {on && key === 'guardian' ? (
                <div className={clsx(styles.grid, styles.personBody)}>
                  <TextField
                    id="guardian-rel"
                    label="Relationship (e.g. aunt, grandparent)"
                    required
                    value={form.guardianRelationship}
                    error={errors.guardianRelationship}
                    onChange={value => {
                      patch({ guardianRelationship: value });
                      if (value.trim()) setErrors(current => clearError(current, 'guardianRelationship'));
                    }}
                  />
                  <TextField
                    id="guardian-name"
                    label="Full name"
                    required
                    value={form.guardianName}
                    error={errors.guardianName}
                    onChange={value => {
                      patch({ guardianName: value });
                      if (value.trim()) setErrors(current => clearError(current, 'guardianName'));
                    }}
                  />
                  <TextField id="guardian-phone" label="Cell phone" type="tel" value={form.guardianPhoneNumber} error={errors.guardianPhoneNumber} onChange={value => changePhone('guardianPhoneNumber', 'guardianPhone', value)} />
                  <TextField id="guardian-email" label="Email" type="email" value={form.guardianEmail} error={errors.guardianEmail} onChange={value => changeEmail('guardianEmail', 'guardianEmail', value)} />
                  <TextField id="guardian-job" label="Job / occupation" value={form.guardianOccupation} onChange={value => patch({ guardianOccupation: value })} />
                  <TextField id="guardian-employer" label="Employer" value={form.guardianEmployer} onChange={value => patch({ guardianEmployer: value })} />
                  <SelectField id="guardian-education" label="Highest grade completed" value={form.guardianEducation || null} options={educationOptions} onChange={value => patch({ guardianEducation: value || '' })} />
                  <TextField id="guardian-birth" label="Place of birth" value={form.guardianPlaceOfBirth} onChange={value => patch({ guardianPlaceOfBirth: value })} />
                  <div className={styles.span}>
                    <ChoiceChips label="Annual income range" options={salaryOptions} value={form.guardianSalaryChoice} disabled={readOnly} onChange={value => patch({ guardianSalaryChoice: value })} />
                    {keptLegacyValue(form.guardianSalary, SCLA_SALARY_RANGES) ? <p className={styles.legacy}>{`Previous answer: ${form.guardianSalary}`}</p> : null}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </fieldset>
    </SclaStepCard>
  );
}
